import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';

export type InventoryTransferStatus = $Enums.InventoryTransferStatus;

export type InventoryTransferFacts = {
  /** The transfer's identity, retained for callers that project the lifecycle facts. */
  id: number;
  status: InventoryTransferStatus;
  /** The ship step stamps a transfer date; a transfer that carries one cannot ship again. */
  hasTransferDate: boolean;
};

/**
 * The header fields a transfer surface can write. The page edits contact and the shipment
 * (ship from / to, temperatures, dates); the API also accepts `transferDate`; `expenses` is the
 * charges window the IT charge policy imports.
 */
export const INVENTORY_TRANSFER_FIELDS = [
  'contact',
  'shipFrom',
  'shipTo',
  'temperatureMin',
  'temperatureMax',
  'shipDate',
  'deliveryDate',
  'transferDate',
  'expenses',
] as const;

export type InventoryTransferField = (typeof INVENTORY_TRANSFER_FIELDS)[number];

/** The lifecycle operations, one per state-machine event (`inventoryTransferLifecycleOperation`). */
export const INVENTORY_TRANSFER_OPS = ['submit', 'mark as shipped', 'close', 'revert'] as const;

export type InventoryTransferOp = (typeof INVENTORY_TRANSFER_OPS)[number];

/**
 * The events the Inventory Transfer state machine accepts
 * (api/src/services/inventoryTransfers/inventoryTransfer.states.ts), kept here so the api door and
 * the web button ask the policy the SAME question about the SAME event.
 */
export const INVENTORY_TRANSFER_LIFECYCLE_EVENTS = ['submit', 'mark_as_shipped', 'close', 'revert'] as const;

export type InventoryTransferLifecycleEvent = (typeof INVENTORY_TRANSFER_LIFECYCLE_EVENTS)[number];

/** ONE event → operation mapping; the op id is the event with `_` read as a space. */
export const inventoryTransferLifecycleOperation = (event: InventoryTransferLifecycleEvent): InventoryTransferOp => {
  switch (event) {
    case 'submit':
      return 'submit';
    case 'mark_as_shipped':
      return 'mark as shipped';
    case 'close':
      return 'close';
    case 'revert':
      return 'revert';
  }
};

/** The statuses in which a transfer's header, lots and charges can change. */
const EDITABLE: readonly InventoryTransferStatus[] = ['DRAFT', 'SUBMITTED'];

/** The statuses in which a transfer's header fields can change. */
export const INVENTORY_TRANSFER_HEADER_EDITABLE_STATUSES: readonly InventoryTransferStatus[] = EDITABLE;

/** The statuses in which a transfer's lots (items and their pallet tags) can change. */
export const INVENTORY_TRANSFER_ITEMS_EDITABLE_STATUSES: readonly InventoryTransferStatus[] = EDITABLE;

/** The statuses in which a transfer's charges can change. */
export const INVENTORY_TRANSFER_EXPENSES_EDITABLE_STATUSES: readonly InventoryTransferStatus[] = EDITABLE;

/**
 * THE STATE MACHINE IS THE AUTHORITY ON LIFECYCLE AVAILABILITY.
 *
 *   draft      { submit }
 *   submitted  { mark_as_shipped, revert }
 *   shipped    { close }        revert has its own refusal: nothing reverses a shipment
 *   closed · error              final states, nothing is accepted
 *
 * Pinned cell by cell against the machine in
 * api/src/services/inventoryTransfers/inventoryTransfer.lifecycle.parity.test.ts.
 */
const ACCEPTED_OPS: Record<InventoryTransferStatus, readonly InventoryTransferOp[]> = {
  DRAFT: ['submit'],
  SUBMITTED: ['mark as shipped', 'revert'],
  SHIPPED: ['close'],
  CLOSED: [],
  ERROR: [],
};

/** The transfer header labels its shipping action Confirm Shipped. */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  submit: 'Submit',
  'mark as shipped': 'Confirm Shipped',
  close: 'Close',
  revert: 'Revert',
};

export const inventoryTransferInvalidEvent = (status: InventoryTransferStatus, op: string) =>
  deny(
    'INVENTORY_TRANSFER_INVALID_EVENT',
    `${ACTION_LABELS[op] ?? 'This action'} isn't available while the inventory transfer is ${getStatusLabel(status)}.`
  );

const decideOperation = (facts: InventoryTransferFacts, op: InventoryTransferOp): Decision => {
  if (facts.status === 'SHIPPED' && op === 'revert') {
    return deny('INVENTORY_TRANSFER_REVERT_SHIPPED', "Shipped transfers can't be reverted.");
  }

  if (!ACCEPTED_OPS[facts.status].includes(op)) {
    return inventoryTransferInvalidEvent(facts.status, op);
  }

  if (op === 'mark as shipped' && facts.hasTransferDate) {
    return deny('INVENTORY_TRANSFER_ALREADY_SHIPPED', 'This transfer was already shipped.');
  }

  return allow();
};

type TransferAction = Action<InventoryTransferField, InventoryTransferOp>;

const decideEntity = (facts: InventoryTransferFacts, action: TransferAction): Decision => {
  switch (action.type) {
    case 'view':
    case 'add':
    case 'edit':
      return allow();
    case 'delete':
      return EDITABLE.includes(facts.status)
        ? allow()
        : deny('INVENTORY_TRANSFER_DELETE_LOCKED', 'Only Draft or Submitted transfers can be deleted.');
    case 'perform':
      return decideOperation(facts, action.op);
  }
};

const fieldRule = (facts: InventoryTransferFacts, action: TransferAction): Decision =>
  action.type !== 'edit' || EDITABLE.includes(facts.status)
    ? allow()
    : deny(
        'INVENTORY_TRANSFER_FIELD_LOCKED',
        `This field is locked once the inventory transfer is ${getStatusLabel(facts.status)}.`
      );

const fieldRules = {
  contact: fieldRule,
  shipFrom: fieldRule,
  shipTo: fieldRule,
  temperatureMin: fieldRule,
  temperatureMax: fieldRule,
  shipDate: fieldRule,
  deliveryDate: fieldRule,
  transferDate: fieldRule,
  expenses: fieldRule,
} satisfies Record<InventoryTransferField, typeof fieldRule>;

export const inventoryTransferPolicy: Policy<InventoryTransferFacts, InventoryTransferField, InventoryTransferOp> =
  definePolicy<InventoryTransferFacts, InventoryTransferField, InventoryTransferOp>({
    kind: 'inventory transfer',
    fields: INVENTORY_TRANSFER_FIELDS,
    ops: INVENTORY_TRANSFER_OPS,
    requires: {},
    entity: decideEntity,
    field: fieldRules,
  });
